import type { SVGProps } from "react";

type IconProps = Omit<SVGProps<SVGSVGElement>, "ref">;

/**
 * Every glyph here is copied path for path from the production build at
 * https://adbond.sa/wajihat-qurtuba/, which renders the same Figma file. Strokes
 * use currentColor so the surrounding text colour drives them.
 */

/** Play circle, 20x20, white on the gradient video CTA. */
export function IconPlay(props: IconProps) {
  return (
    <svg viewBox="0 0 20 20" width="1em" height="1em" aria-hidden="true" {...props}>
      <path
        d="M10 18.5C14.6944 18.5 18.5 14.6944 18.5 10C18.5 5.30558 14.6944 1.5 10 1.5C5.30558 1.5 1.5 5.30558 1.5 10C1.5 14.6944 5.30558 18.5 10 18.5Z"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="none"
      />
      <path d="M8 6.90002L13.2 10L8 13.1V6.90002Z" fill="currentColor" />
    </svg>
  );
}

/** Brochure page, 20x20, purple on the white brochure CTA. */
export function IconBrochure(props: IconProps) {
  return (
    <svg viewBox="0 0 20 20" width="1em" height="1em" aria-hidden="true" {...props}>
      <path
        d="M5.5 2.75H11.6L15 6.15V17.25H5.5V2.75Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
        fill="none"
      />
      <path
        d="M11.5 2.90002V6.30002H14.9M7.69995 10H12.8M7.69995 12.8H12.8"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

/** Folded map, 18x18, white on the solid interactive-platform CTA. */
export function IconPlatform(props: IconProps) {
  return (
    <svg viewBox="0 0 18 18" width="1em" height="1em" aria-hidden="true" {...props}>
      <path
        d="M2.5 4.25L6.75 2.5L11.25 4.25L15.5 2.5V13.75L11.25 15.5L6.75 13.75L2.5 15.5V4.25Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
        fill="none"
      />
      <path
        d="M6.75 2.5V13.75M11.25 4.25V15.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

/** Location pin, 18x18, white on the solid project-location CTA. */
export function IconLocation(props: IconProps) {
  return (
    <svg viewBox="0 0 18 18" width="1em" height="1em" aria-hidden="true" {...props}>
      <path
        d="M9 16C9 16 14 11.35 14 7C14 5.67392 13.4732 4.40215 12.5355 3.46447C11.5979 2.52678 10.3261 2 9 2C7.67392 2 6.40215 2.52678 5.46447 3.46447C4.52678 4.40215 4 5.67392 4 7C4 11.35 9 16 9 16Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
        fill="none"
      />
      <path
        d="M8.99995 8.80001C9.99406 8.80001 10.8 7.99412 10.8 7.00001C10.8 6.0059 9.99406 5.20001 8.99995 5.20001C8.00584 5.20001 7.19995 6.0059 7.19995 7.00001C7.19995 7.99412 8.00584 8.80001 8.99995 8.80001Z"
        stroke="currentColor"
        strokeWidth="1.6"
        fill="none"
      />
    </svg>
  );
}

/** Parking meter and car, 28x28, purple stat icon. */
export function IconParking(props: IconProps) {
  return (
    <svg viewBox="0 0 28 28" width="1em" height="1em" aria-hidden="true" {...props}>
      <path
        d="M11.6666 4.08331H6.41659C5.12792 4.08331 4.08325 5.12798 4.08325 6.41665V15.1666C4.08325 16.4553 5.12792 17.5 6.41659 17.5H11.6666C12.9552 17.5 13.9999 16.4553 13.9999 15.1666V6.41665C13.9999 5.12798 12.9552 4.08331 11.6666 4.08331Z"
        stroke="currentColor"
        strokeWidth="2.1"
        fill="none"
      />
      <path
        d="M7.58325 14V7.58331H9.91659C10.5354 7.58331 11.1289 7.82915 11.5665 8.26673C12.0041 8.70432 12.2499 9.29781 12.2499 9.91665C12.2499 10.5355 12.0041 11.129 11.5665 11.5666C11.1289 12.0041 10.5354 12.25 9.91659 12.25H7.58325"
        stroke="currentColor"
        strokeWidth="2.1"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      <path
        d="M16.3333 19.8333H22.4L23.9167 22.4V23.9166H14.8167V22.4L16.3333 19.8333Z"
        stroke="currentColor"
        strokeWidth="2.1"
        strokeLinejoin="round"
        fill="none"
      />
      <path
        d="M16.8 19.8333L17.5 17.5H21.1167L21.9334 19.8333"
        stroke="currentColor"
        strokeWidth="2.1"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

/** Airport road, 28x28, purple stat icon. */
export function IconRoad(props: IconProps) {
  return (
    <svg viewBox="0 0 28 28" width="1em" height="1em" aria-hidden="true" {...props}>
      <path
        d="M13.9999 3.5V11.9M16.9166 8.16667L13.9999 3.5L11.0833 8.16667"
        stroke="currentColor"
        strokeWidth="2.1"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      <path
        d="M5.83325 23.3334L11.3166 13.4167M22.1666 23.3334L16.6833 13.4167"
        stroke="currentColor"
        strokeWidth="2.1"
        strokeLinecap="round"
        fill="none"
      />
      <path
        d="M13.0667 15.1667H14.9334M12.8334 18.6667H15.1667M12.4834 22.1667H15.5167"
        stroke="currentColor"
        strokeWidth="2.1"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

/** Metro car, 28x28, purple stat icon. */
export function IconMetro(props: IconProps) {
  return (
    <svg viewBox="0 0 28 28" width="1em" height="1em" aria-hidden="true" {...props}>
      <path
        d="M18.6666 4.08331H9.33325C7.40026 4.08331 5.83325 5.65032 5.83325 7.58331V16.9166C5.83325 18.8496 7.40026 20.4166 9.33325 20.4166H18.6666C20.5996 20.4166 22.1666 18.8496 22.1666 16.9166V7.58331C22.1666 5.65032 20.5996 4.08331 18.6666 4.08331Z"
        stroke="currentColor"
        strokeWidth="2.1"
        fill="none"
      />
      <path
        d="M9.33325 8.16669H18.6666V12.8334H9.33325V8.16669Z"
        stroke="currentColor"
        strokeWidth="2.1"
        fill="none"
      />
      <path
        d="M10.4999 17.5C11.1443 17.5 11.6666 16.9777 11.6666 16.3334C11.6666 15.689 11.1443 15.1667 10.4999 15.1667C9.85559 15.1667 9.33325 15.689 9.33325 16.3334C9.33325 16.9777 9.85559 17.5 10.4999 17.5Z"
        fill="currentColor"
      />
      <path
        d="M17.4999 17.5C18.1443 17.5 18.6666 16.9777 18.6666 16.3334C18.6666 15.689 18.1443 15.1667 17.4999 15.1667C16.8556 15.1667 16.3333 15.689 16.3333 16.3334C16.3333 16.9777 16.8556 17.5 17.4999 17.5Z"
        fill="currentColor"
      />
      <path
        d="M9.33341 23.3334L11.6667 20.4167M18.6667 20.4167L21.0001 23.3334M8.16675 23.3334H19.8334"
        stroke="currentColor"
        strokeWidth="2.1"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

/** Gear, 30x30, purple feature icon. */
export function IconOperational(props: IconProps) {
  return (
    <svg viewBox="0 0 30 30" width="1em" height="1em" aria-hidden="true" {...props}>
      <path
        d="M15 5.89282C12.5846 5.89282 10.2682 6.85232 8.56024 8.56024C6.85232 10.2682 5.89282 12.5846 5.89282 15C5.89282 17.4153 6.85232 19.7318 8.56024 21.4397C10.2682 23.1476 12.5846 24.1071 15 24.1071C17.4153 24.1071 19.7318 23.1476 21.4397 21.4397C23.1476 19.7318 24.1071 17.4153 24.1071 15C24.1071 12.5846 23.1476 10.2682 21.4397 8.56024C19.7318 6.85232 17.4153 5.89282 15 5.89282Z"
        stroke="currentColor"
        strokeWidth="2"
        fill="none"
      />
      <path
        d="M15 3V5.57143M15 24.4286V27M3 15H5.57143M24.4286 15H27M6.64286 6.64286L8.46429 8.46429M21.5357 21.5357L23.3571 23.3571M23.3571 6.64286L21.5357 8.46429M8.46429 21.5357L6.64286 23.3571"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        fill="none"
      />
      <path
        d="M15.0001 17.6786C16.4794 17.6786 17.6787 16.4793 17.6787 15C17.6787 13.5206 16.4794 12.3214 15.0001 12.3214C13.5208 12.3214 12.3215 13.5206 12.3215 15C12.3215 16.4793 13.5208 17.6786 15.0001 17.6786Z"
        stroke="currentColor"
        strokeWidth="2"
        fill="none"
      />
    </svg>
  );
}

/** Flexible corners, 30x30, purple feature icon. */
export function IconFlexible(props: IconProps) {
  return (
    <svg viewBox="0 0 30 30" width="1em" height="1em" aria-hidden="true" {...props}>
      <path
        d="M5.35718 10.7143V5.35718H10.7143M19.2858 5.35718H24.6429V10.7143M24.6429 19.2858V24.6429H19.2858M10.7143 24.6429H5.35718V19.2858"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      <path
        d="M11.2501 11.2499L6.96436 6.96423M18.7501 11.2499L23.0358 6.96423M18.7501 18.7499L23.0358 23.0357M11.2501 18.7499L6.96436 23.0357"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

/** Pin with sparkle, 30x30, purple feature icon. */
export function IconPrime(props: IconProps) {
  return (
    <svg viewBox="0 0 30 30" width="1em" height="1em" aria-hidden="true" {...props}>
      <path
        d="M15.0001 24.6429C15.0001 24.6429 21.4287 19.1786 21.4287 13.7143C21.4287 12.8701 21.2624 12.0342 20.9393 11.2542C20.6163 10.4743 20.1427 9.7656 19.5458 9.16865C18.9488 8.5717 18.2402 8.09818 17.4602 7.77511C16.6803 7.45205 15.8443 7.28577 15.0001 7.28577C14.1559 7.28577 13.3199 7.45205 12.54 7.77511C11.76 8.09818 11.0514 8.5717 10.4544 9.16865C9.85747 9.7656 9.38395 10.4743 9.06088 11.2542C8.73781 12.0342 8.57153 12.8701 8.57153 13.7143C8.57153 19.1786 15.0001 24.6429 15.0001 24.6429Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      <path
        d="M15 15.7501C16.3018 15.7501 17.3571 14.6947 17.3571 13.3929C17.3571 12.0911 16.3018 11.0358 15 11.0358C13.6982 11.0358 12.6428 12.0911 12.6428 13.3929C12.6428 14.6947 13.6982 15.7501 15 15.7501Z"
        stroke="currentColor"
        strokeWidth="2"
        fill="none"
      />
      <path
        d="M23.0356 5.57141V8.7857M21.4285 7.17855H24.6428"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

/** WhatsApp glyph, 29x29, white on the green disc. */
export function IconWhatsapp(props: IconProps) {
  return (
    <svg viewBox="0 0 29 29" width="1em" height="1em" aria-hidden="true" {...props}>
      <path
        d="M21.112 17.3782C20.7531 17.1982 18.9877 16.3306 18.6591 16.2098C18.3292 16.0902 18.0899 16.0309 17.8495 16.391C17.6102 16.7499 16.9215 17.5595 16.7124 17.7999C16.5034 18.0392 16.2931 18.0694 15.9343 17.8906C15.5754 17.7105 14.4178 17.3311 13.0463 16.1083C11.9794 15.1561 11.258 13.9804 11.049 13.6203C10.8399 13.2614 11.0272 13.0669 11.2061 12.8881C11.368 12.7274 11.5661 12.4688 11.745 12.2597C11.925 12.0495 11.9842 11.8997 12.1051 11.6604C12.2247 11.4212 12.1655 11.2109 12.0748 11.0309C11.9854 10.8508 11.2665 9.08303 10.968 8.36407C10.6756 7.66444 10.3796 7.7599 10.1596 7.74782C9.9506 7.73815 9.71014 7.73574 9.47089 7.73574C9.23164 7.73574 8.84256 7.82515 8.51268 8.18524C8.18281 8.54411 7.25601 9.4129 7.25601 11.1807C7.25601 12.9485 8.54289 14.6559 8.72172 14.8951C8.90176 15.1344 11.2532 18.7618 14.8564 20.3169C15.7131 20.6867 16.3826 20.9078 16.9033 21.0733C17.7637 21.3464 18.5467 21.3077 19.1653 21.2159C19.8553 21.1132 21.2896 20.3471 21.5881 19.5085C21.8877 18.6699 21.8877 17.951 21.7971 17.8012C21.7077 17.6513 21.4684 17.5619 21.1083 17.3819M14.558 26.3272H14.5531C12.4141 26.3273 10.3144 25.7522 8.47401 24.6621L8.03781 24.4035L3.51743 25.5901L4.72335 21.1821L4.43939 20.7302C3.24367 18.8261 2.61111 16.6227 2.61481 14.3743C2.61722 7.7889 7.97618 2.42994 14.5652 2.42994C16.1349 2.42635 17.6896 2.73422 19.1395 3.33572C20.5893 3.93723 21.9054 4.82041 23.0115 5.93411C24.1247 7.04178 25.0071 8.35929 25.6075 9.81036C26.208 11.2614 26.5146 12.8172 26.5096 14.3876C26.5072 20.973 21.1482 26.332 14.564 26.332M24.7297 4.21949C23.3983 2.87804 21.8139 1.81431 20.0682 1.08998C18.3225 0.36566 16.4504 -0.00483697 14.5604 -1.45577e-05C6.63976 -1.45577e-05 0.193306 6.44644 0.189681 14.3707C0.183351 16.8933 0.844973 19.3726 2.10731 21.5567L0.0688477 29L7.68618 27.0014C9.79349 28.149 12.1548 28.7501 14.5543 28.7499H14.5604C22.481 28.7499 28.9275 22.3034 28.9311 14.3792C28.9375 12.4909 28.5692 10.6201 27.8477 8.87514C27.1261 7.13018 26.0655 5.5457 24.7273 4.21344L24.7297 4.21949Z"
        fill="currentColor"
      />
    </svg>
  );
}

/** Instagram glyph, 24x24, monochrome for the contact pills. */
export function IconInstagram(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width="1em" height="1em" aria-hidden="true" {...props}>
      <path
        d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z"
        fill="currentColor"
      />
    </svg>
  );
}

/** LinkedIn glyph, 24x24, monochrome for the contact pills. */
export function IconLinkedin(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width="1em" height="1em" aria-hidden="true" {...props}>
      <path
        d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"
        fill="currentColor"
      />
    </svg>
  );
}

/** Left arrow, 16x16, purple tile inside the WhatsApp card. */
export function IconArrow(props: IconProps) {
  return (
    <svg viewBox="0 0 16 16" width="1em" height="1em" aria-hidden="true" {...props}>
      <path
        d="M12.6666 8H3.33325M7.33325 12L3.33325 8L7.33325 4"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

/** Globe, 18x18, purple bullet glyph in the location card. */
export function IconBullet(props: IconProps) {
  return (
    <svg viewBox="0 0 18 18" width="1em" height="1em" aria-hidden="true" {...props}>
      <circle cx="9" cy="9" r="7" stroke="currentColor" strokeWidth="1.5" fill="none" />
      <path
        d="M2 9H16M9 2C11 4 12 6.5 12 9C12 11.5 11 14 9 16C7 14 6 11.5 6 9C6 6.5 7 4 9 2Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

/** Down chevron, 16x16, grey, drawn inside the mobile area select. */
export function IconChevron(props: IconProps) {
  return (
    <svg viewBox="0 0 16 16" width="1em" height="1em" aria-hidden="true" {...props}>
      <path
        d="M3.5 6L8 10.5L12.5 6"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}
